// src/features/student-management/ui/organisms/PersonResumeExams/PersonResumeExams.tsx
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/atoms';
import { DataTable } from '@/shared/ui/organisms/DataTable';
import type { ResumeCourseExam, ResumeExamCourse } from '../../../types/resume';

/**
 * Props do organismo PersonResumeExams.
 */
export interface PersonResumeExamsProps {
  readonly examCourses: readonly ResumeExamCourse[];
  readonly isLoading?: boolean;
}

/**
 * Seção de Provas do resumo do aluno.
 *
 * Exibe um accordion por curso, cada um com uma tabela de provas interna.
 *
 * @param examCourses - Lista de cursos com provas associadas
 * @param isLoading - Exibe skeleton quando verdadeiro
 */
export function PersonResumeExams({ examCourses, isLoading }: PersonResumeExamsProps) {
  const { t } = useTranslation('personManagement');

  const examColumns = useMemo<ColumnDef<ResumeCourseExam, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('resumePage.examsTable.columns.name'),
        cell: info => (
          <span className="ts-paragraph-xs !font-medium">{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'disciplineName',
        header: t('resumePage.examsTable.columns.discipline'),
        cell: info => <span className="ts-paragraph-xs">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'grade',
        header: t('resumePage.examsTable.columns.grade'),
        cell: info => {
          const grade = info.getValue() as number | null;
          return <span className="ts-paragraph-xs">{grade !== null ? grade.toFixed(1) : '—'}</span>;
        },
      },
      {
        accessorKey: 'tryCount',
        header: t('resumePage.examsTable.columns.tryCount'),
        cell: info => <span className="ts-paragraph-xs">{String(info.getValue() as number)}</span>,
      },
      {
        accessorKey: 'isRecovery',
        header: t('resumePage.examsTable.columns.isRecovery'),
        cell: info => (
          <span className="ts-paragraph-xs">
            {(info.getValue() as boolean)
              ? t('resumePage.examsTable.isRecovery.yes')
              : t('resumePage.examsTable.isRecovery.no')}
          </span>
        ),
      },
      {
        accessorKey: 'lastTryDate',
        header: t('resumePage.examsTable.columns.lastTryDate'),
        cell: info => {
          const date = info.getValue() as string | null;
          return (
            <span className="ts-paragraph-xs">
              {date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—'}
            </span>
          );
        },
      },
    ],
    [t],
  );

  return (
    <section className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level1">
      <h2 className="mb-mini ts-subtitle-sm text-ctx-content-title">
        {t('resumePage.sections.exams')}
      </h2>
      {examCourses.length === 0 ? (
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('resumePage.sections.noExams')}
        </p>
      ) : (
        <Accordion type="multiple" className="flex flex-col gap-mini">
          {examCourses.map((course, index) => {
            const averageLabel =
              course.averageGrade !== null
                ? t('resumePage.examsTable.averageLabel', {
                    value: course.averageGrade.toFixed(1),
                  })
                : '—';

            return (
              <AccordionItem
                key={`${course.courseName}-${String(index)}`}
                value={`course-${String(index)}`}
                className="overflow-hidden rounded-xs border-hairline border-ctx-layout-border"
              >
                <AccordionTrigger className="px-xs py-mini hover:bg-ctx-layout-body">
                  <div className="flex flex-1 items-center justify-between gap-xs">
                    <div className="flex flex-col gap-quark">
                      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                        {course.courseName}
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {course.classStudentName}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-xs">
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {averageLabel}
                      </span>
                      <ChevronDownIcon className="size-xxs text-ctx-content-placeholder transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-xs pb-xs pt-mini">
                  <DataTable
                    data={course.exams as ResumeCourseExam[]}
                    columns={examColumns}
                    totalCount={course.exams.length}
                    {...(isLoading !== undefined && { isLoading })}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </section>
  );
}
